import React, { useEffect, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Alert, Autocomplete } from '@material-ui/lab'
import { useTheme } from '@material-ui/styles'

import { Card, LoadingFeedback } from 'components'
import { ManageOrganizationsTable } from './components'

import { useFetch, useSnackbar, usePagination } from 'hooks'

import helpers from 'helpers'
import * as service from 'service'
import schema from './schema'

const ManageOrganizationsForm = ({
  ssoCorporateGroup,
  show,
  setRefreshAll,
  refreshAll,
}) => {
  const [organizationInputValue, setOrganizationInputValue] = useState('')
  const [companies, setOrganizations] = useState([])
  const [loadingOrganizations, setLoadingOrganizations] = useState(false)

  const snackbar = useSnackbar()

  const handleTypeOrganizationsInput = (e) => {
    setOrganizationInputValue(e.target.value)
  }
  const { handleSubmit, control, reset, errors } = useForm({
    validationSchema: schema,
  })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
    defaultMatches: true,
  })
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(5)

  const { instructionsText } = helpers.companies.typeAndSearch

  const { response, isLoading, refresh } = useFetch(
    service.dponet.ssoCorporateGroupOrganizations.get,
    {
      ssoCorporateGroupId: ssoCorporateGroup?.id,
      perPage,
      page,
      minimal: true,
    },
    [perPage, page, ssoCorporateGroup, refreshAll],
  )

  const handleRefresh = () => {
    setRefreshAll(!refreshAll)
    refresh()
  }

  const onSubmit = async (data) => {
    try {
      setLoadingOrganizations(true)
      await service.dponet.ssoCorporateGroupOrganizations.create({
        ssoCorporateGroupId: ssoCorporateGroup.id,
        organizationId: data.organizationId,
      })
      snackbar.open({
        message: 'Organização vinculada com sucesso!',
        variant: 'success',
      })
      setRefreshAll(!refreshAll)
      reset()
    } catch (error) {
      return snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error || 'Erro ao vincular organização!',
        ),
        variant: 'error',
      })
    } finally {
      setLoadingOrganizations(false)
    }
  }

  const loadOrganizations = async () => {
    setLoadingOrganizations(true)
    const organizationsResponse = await service.backoffice.organizations.get({
      minimal: true,
      perPage: 1000 * 1000,
      name: organizationInputValue,
      authorEmail: ssoCorporateGroup?.domain,
    })
    setOrganizations(organizationsResponse?.data?.organizations || [])
    setLoadingOrganizations(false)
  }

  useEffect(() => {
    const getCompanies = setTimeout(() => {
      if (organizationInputValue.length < 3) return

      loadOrganizations()
    }, 1000)

    return () => clearTimeout(getCompanies)
    // eslint-disable-next-line
  }, [organizationInputValue])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingFeedback open={isLoading} />
      <Card title={'Gerenciamento de Organizações'}>
        {!show && (
          <Grid container spacing={2}>
            <Grid item xs={isMobile ? 12 : 10}>
              <Box mt={1}>
                <Controller
                  control={control}
                  name="organizationId"
                  onChange={([, data]) => data?.id}
                  mode="onChange"
                  as={
                    <Autocomplete
                      options={companies || []}
                      noOptionsText={instructionsText(organizationInputValue)}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onChange={handleTypeOrganizationsInput}
                          label="Vincular organização"
                          variant="outlined"
                          error={!!errors.organizationId}
                          helperText={errors?.organizationId?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingOrganizations && (
                                  <CircularProgress color="primary" size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={isMobile ? 12 : 2}>
              <Box
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                >
                  Adicionar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography>
                  Ao vincular uma organização, todos os usuários com e-mails que
                  compartilham o mesmo domínio do Grupo Empresarial SSO-SAML
                  serão automaticamente associados a esse grupo.
                </Typography>
              </Alert>
            </Grid>
            {/* Implementar em uma V2
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={allUsers} onChange={handleAllUsers} />
                }
                label="Vincular todas os usários dessa organização?"
              />
            </Grid> */}
          </Grid>
        )}
        <ManageOrganizationsTable
          ssoCorporateGroupId={ssoCorporateGroup?.id}
          ssoCorporateGroupOrganizations={
            response?.data?.ssoCorporateGroupOrganizations
          }
          totalCount={response?.data?.meta?.totalCount}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          perPage={perPage}
          show={show}
          refresh={handleRefresh}
        />
      </Card>
    </form>
  )
}

export default ManageOrganizationsForm
